/**
 * ASR Settings Service
 * TASK-426: Per-tenant ASR provider toggle (FINMA local-Whisper tier).
 *
 * Reads/writes the account's speech-to-text configuration (provider/model/
 * language) via the auth-service admin API, proxied through the gateway.
 */

import { httpClient } from '../../lib/httpClient';
import { logger } from '../../utils/logger';
import type { ASRProvider, ASRSettings, ASRSettingsUpdateData } from '../../types/admin';

class ASRSettingsService {
  private readonly basePath = '/api/admin/asr';

  /** Get the ASR configuration for the current account. */
  async getASRSettings(): Promise<ASRSettings> {
    try {
      return await httpClient.get<ASRSettings>(`${this.basePath}/settings`);
    } catch (error) {
      logger.error('Error fetching ASR settings:', error);
      throw error;
    }
  }

  /** Update the ASR configuration. Only provided fields change. */
  async updateASRSettings(updates: ASRSettingsUpdateData): Promise<ASRSettings> {
    try {
      return await httpClient.put<ASRSettings>(`${this.basePath}/settings`, updates);
    } catch (error) {
      logger.error('Error updating ASR settings:', error);
      throw error;
    }
  }

  /** Allowed models per provider (drives the model picker). */
  async getModels(): Promise<Record<ASRProvider, string[]>> {
    try {
      const data = await httpClient.get<{ models: Record<ASRProvider, string[]> }>(
        `${this.basePath}/models`,
      );
      return data.models;
    } catch (error) {
      logger.error('Error fetching ASR models:', error);
      throw error;
    }
  }
}

export const asrSettingsService = new ASRSettingsService();
